/**
 * tokenManager — ensures a valid (non-expired) Keycloak token before API calls.
 *
 * Uses the Keycloak singleton directly so it can be called from both
 * React hooks and plain service modules.
 *
 * `getValidToken()` checks if the current token will expire within
 * the next 30 seconds and proactively refreshes it if needed.
 */

import keycloak from '../keycloak';

/** Minimum remaining validity in seconds before we trigger a refresh */
const MIN_VALIDITY_SECS = 30;

/** Max retries for token refresh before giving up */
const MAX_REFRESH_RETRIES = 2;

/** Delay between retries in ms */
const RETRY_DELAY_MS = 1000;

/**
 * Returns a valid access token, refreshing first if it's about to expire.
 * Retries on transient failures before falling back to login redirect.
 */
export async function getValidToken(): Promise<string> {
  if (!keycloak.authenticated) {
    throw new Error('Not authenticated');
  }

  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      await keycloak.updateToken(MIN_VALIDITY_SECS);
      if (keycloak.token) return keycloak.token;
    } catch (err) {
      lastError = err;
      if (attempt < MAX_REFRESH_RETRIES) {
        console.warn(`🔐 Token refresh attempt ${attempt + 1} failed, retrying...`);
        await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      }
    }
  }

  // All retries exhausted — force re-login
  console.error('🔐 Token refresh failed after retries, redirecting to login', lastError);
  keycloak.login();
  throw new Error('Token refresh failed');
}
